import type { TaskDTO, TaskViewMode } from '@taskos/shared';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CalendarDays, Hourglass, Inbox, LayoutGrid, List, Pin, Repeat, Rows3, Zap } from 'lucide-react';
import { Link } from 'react-router';
import { patch } from '../api/client';
import { useDashboard } from '../api/queries';
import { useSession } from '../api/session';
import { useUI } from '../app/ui';
import { Button, Empty, ErrorState, Segmented, Skeleton } from '../components/ui';
import { SnoozeAllButton } from '../features/tasks/Dialogs';
import { TaskCollection } from '../features/tasks/TaskCollection';
import { formatDate } from '../lib/format';

export const VIEW_OPTIONS = [
  { value: 'TILES' as const, label: <><LayoutGrid size={15} aria-hidden /> Tiles</>, title: 'Tiles' },
  { value: 'LIST' as const, label: <><List size={15} aria-hidden /> List</>, title: 'List' },
  { value: 'CALENDAR' as const, label: <><CalendarDays size={15} aria-hidden /> Calendar</>, title: 'Calendar' },
  { value: 'TIMELINE' as const, label: <><Rows3 size={15} aria-hidden /> Timeline</>, title: 'Timeline' },
];

export function useViewMode(): [TaskViewMode, (m: TaskViewMode) => void] {
  const { me } = useSession();
  const qc = useQueryClient();
  const set = (m: TaskViewMode) => {
    qc.setQueryData(['me'], { ...me, preferences: { ...me.preferences, defaultView: m } });
    void patch('/api/me/preferences', { defaultView: m }).catch(() => undefined);
  };
  return [me.preferences.defaultView, set];
}

/** A dashboard section; `items` already excludes tasks shown in an earlier section. */
function Section({ title, emoji, items: fresh, count, to, mode }: { title: string; emoji: string; items: TaskDTO[]; count: number; to: string; mode: TaskViewMode }) {
  if (!fresh.length) return null;
  const hidden = count - fresh.length;
  return (
    <section className="section" aria-labelledby={`sec-${to}`}>
      <div className="section-head">
        <h2 id={`sec-${to}`}>
          <span aria-hidden>{emoji}</span> {title} <span className="badge">{count}</span>
        </h2>
        {hidden > 0 && (
          <Link to={to} className="small section-link">
            View all {count} →
          </Link>
        )}
      </div>
      <TaskCollection items={fresh} mode={mode === 'CALENDAR' ? 'LIST' : mode} emptyTitle="" />
    </section>
  );
}

const STATS = [
  { key: 'overdue', to: '/overdue', label: 'Overdue', icon: AlertTriangle, tone: 'danger' },
  { key: 'dueToday', to: '/today', label: 'Due today', icon: Zap, tone: 'primary' },
  { key: 'waiting', to: '/waiting', label: 'Waiting', icon: Hourglass, tone: 'warning' },
  { key: 'followUps', to: '/followups', label: 'Follow-ups', icon: Pin, tone: 'info' },
  { key: 'recurring', to: '/recurring', label: 'Recurring', icon: Repeat, tone: 'success' },
  { key: 'inbox', to: '/inbox', label: 'Inbox', icon: Inbox, tone: 'neutral' },
] as const;

export function Dashboard() {
  const d = useDashboard();
  const ui = useUI();
  const [mode, setMode] = useViewMode();
  if (d.isPending) return <Skeleton rows={6} height={80} />;
  if (d.isError) return <ErrorState error={d.error} onRetry={() => d.refetch()} />;
  const { counts: c, sections: s, focus } = d.data;
  const nothing = !focus.length && !c.overdue && !c.dueToday && !c.waiting && !c.followUps;
  // Each task appears once: in Today focus, or else in the first section that lists it.
  const seen = new Set(focus.map((t) => t.id));
  const sections = [
    { title: 'Overdue', emoji: '🔴', items: s.overdue, count: c.overdue, to: '/overdue' },
    { title: 'Due today', emoji: '⚡', items: s.dueToday, count: c.dueToday, to: '/today' },
    { title: 'Waiting', emoji: '⏳', items: s.waiting, count: c.waiting, to: '/waiting' },
    { title: 'Follow-ups', emoji: '📌', items: s.followUps, count: c.followUps, to: '/followups' },
    { title: 'Recurring', emoji: '🔁', items: s.recurring, count: c.recurring, to: '/recurring' },
    { title: 'Staff tasks', emoji: '👥', items: s.staff, count: c.staff, to: '/team' },
  ].map((x) => {
    const items = x.items.filter((t) => !seen.has(t.id));
    for (const t of items) seen.add(t.id);
    return { ...x, items };
  });
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Dashboard</h1>
          <p className="sub">{formatDate(d.data.today, true)}</p>
        </div>
        <div className="inline-row">
          {(c.overdue > 0 || c.dueToday > 0) && <SnoozeAllButton />}
          <Segmented label="View" value={mode} onChange={setMode} options={VIEW_OPTIONS} />
        </div>
      </div>

      <div className="stats">
        {STATS.map((s) => {
          const n = c[s.key];
          const I = s.icon;
          return (
            <Link key={s.key} to={s.to} className={`stat tone-${s.tone} ${s.key === 'overdue' && n ? 'alert' : ''} ${n ? '' : 'zero'}`}>
              <span className="stat-icon" aria-hidden>
                <I size={18} />
              </span>
              <span className="stat-text">
                <span className="n">{n}</span>
                <span className="l">{s.label}</span>
              </span>
            </Link>
          );
        })}
      </div>

      {mode === 'CALENDAR' ? (
        <TaskCollection items={[]} mode="CALENDAR" emptyTitle="" baseQuery={{ view: 'all' }} />
      ) : (
        <>
          <section className="section" aria-labelledby="focus-h">
            <div className="section-head">
              <h2 id="focus-h">🎯 Today focus</h2>
              {c.dueToday + c.overdue > focus.length && (
                <Link to="/today" className="small">
                  More today tasks →
                </Link>
              )}
            </div>
            {nothing ? (
              <Empty icon="🎯" title="No tasks due today." action={<Button variant="primary" onClick={() => ui.openTaskForm({ mode: 'create' })}>Add a task</Button>}>
                Enjoy the clear slate.
              </Empty>
            ) : (
              <TaskCollection items={focus} mode={mode} emptyTitle="Nothing needs your attention right now." emptyIcon="🎉" />
            )}
          </section>
          {sections.map((x) => (
            <Section key={x.to} {...x} mode={mode} />
          ))}
        </>
      )}
    </>
  );
}
